import { capabilitiesForRole, resolveCapabilities } from "../auth/capabilities";

interface LoginCredentials {
  email: string;
  password: string;
  tenant_id?: string;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
  role?: 'admin' | 'staff' | 'customer' | 'farmer' | 'distributor' | 'retailer';
}

interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'staff' | 'customer' | 'farmer' | 'distributor' | 'retailer';
  tenant_id?: string;
  capabilities?: string[];
}

interface AuthResponse {
  success: boolean;
  message: string;
  user?: User;
  token?: string;
}

class AuthService {
  private baseUrl = `${import.meta.env.VITE_API_BASE_URL ?? ''}/accounts/users`;

  private setCookie(name: string, value: string, days: number = 7) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

    const isSecure = window.location.protocol === 'https:';
    const sameSite = isSecure ? 'strict' : 'lax';
    const secureFlag = isSecure ? ' secure;' : '';

    document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/;${secureFlag} samesite=${sameSite}`;
  }

  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  private deleteCookie(name: string) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }

  private setLocal(name: string, value: string) {
    try {
      localStorage.setItem(name, value);
    } catch {
      // ignore
    }
  }

  private getLocal(name: string): string | null {
    try {
      return localStorage.getItem(name);
    } catch {
      return null;
    }
  }

  private getTenantId(explicit?: string): string | undefined {
    if (explicit) return explicit;
    const tenant = this.getLocal('currentTenant');
    if (tenant) {
      try {
        return JSON.parse(tenant).id;
      } catch {
        // ignore
      }
    }
    const envTenant = import.meta.env.VITE_DEFAULT_TENANT_ID as string | undefined;
    return envTenant || undefined;
  }

  private decodeJwt(token: string): Record<string, any> | null {
    try {
      const payload = token.split('.')[1];
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const json = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
          .join('')
      );
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const tenant_id = this.getTenantId(credentials.tenant_id);
      const doLogin = async (tenantId?: string) =>
        fetch(`${this.baseUrl}/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: credentials.email,
            password: credentials.password,
            ...(tenantId ? { tenant_id: tenantId } : {}),
          }),
        });

      let response = await doLogin(tenant_id);
      // Recover from stale tenant context by retrying without tenant scope.
      if (!response.ok && response.status === 401 && tenant_id && !credentials.tenant_id) {
        response = await doLogin(undefined);
      }

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        this.deleteCookie('auth_token');
        this.deleteCookie('user_data');
        this.deleteCookie('current_tenant');
        this.setLocal('auth_token', '');
        this.setLocal('user_data', '');
        this.setLocal('currentTenant', '');
        return { success: false, message: err.detail || 'Login failed' };
      }

      const data = await response.json();
      const token = data.access_token as string | undefined;
      if (!token) {
        this.deleteCookie('auth_token');
        this.deleteCookie('user_data');
        this.setLocal('auth_token', '');
        this.setLocal('user_data', '');
        return { success: false, message: 'Login failed: missing token' };
      }

      const payload = this.decodeJwt(token);
      const user: User = {
        id: payload?.sub ?? 'unknown',
        name: payload?.email ?? credentials.email,
        email: payload?.email ?? credentials.email,
        role: (payload?.role ?? 'staff') as User['role'],
        tenant_id: payload?.tenant_id ?? tenant_id,
        capabilities: resolveCapabilities(payload?.capabilities ?? capabilitiesForRole(payload?.role ?? "staff")),
      };

      this.setCookie('auth_token', token, 7);
      this.setCookie('user_data', JSON.stringify(user), 7);
      this.setLocal('auth_token', token);
      this.setLocal('user_data', JSON.stringify(user));
      if (user.tenant_id) {
        this.setLocal('currentTenant', JSON.stringify({ id: user.tenant_id }));
      }

      return { success: true, message: 'Login successful', user, token };
    } catch {
      return { success: false, message: 'Network error. Please try again.' };
    }
  }

  async register(userData: RegisterData): Promise<AuthResponse> {
    try {
      const tenant_id = this.getTenantId();
      if (!tenant_id) {
        return { success: false, message: 'Tenant is required. Set tenant first.' };
      }

      const response = await fetch(`${this.baseUrl}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userData.email,
          full_name: userData.name,
          password: userData.password,
          role: userData.role ?? 'staff',
          tenant_id,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return { success: false, message: err.detail || 'Registration failed' };
      }

      const user = (await response.json()) as User;
      user.capabilities = resolveCapabilities(user.capabilities ?? capabilitiesForRole(user.role));
      this.setCookie('user_data', JSON.stringify(user), 7);
      this.setLocal('user_data', JSON.stringify(user));
      return { success: true, message: 'Registration successful', user };
    } catch {
      return { success: false, message: 'Network error. Please try again.' };
    }
  }

  async logout(): Promise<void> {
    try {
      await fetch(`${this.baseUrl}/logout`, { method: 'POST' }).catch(() => undefined);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.deleteCookie('auth_token');
      this.deleteCookie('user_data');
      this.setLocal('auth_token', '');
      this.setLocal('user_data', '');
    }
  }

  getCurrentUser(): User | null {
    const normalizeUser = (parsed: User): User => {
      const normalized: User = { ...parsed };
      normalized.capabilities = resolveCapabilities(
        normalized.capabilities ?? capabilitiesForRole(normalized.role)
      );
      return normalized;
    };

    const userData = this.getCookie('user_data');
    if (userData) {
      try {
        return normalizeUser(JSON.parse(userData));
      } catch (error) {
        console.error('Error parsing user data:', error);
        this.deleteCookie('user_data');
      }
    }
    const localUser = this.getLocal('user_data');
    if (localUser) {
      try {
        return normalizeUser(JSON.parse(localUser));
      } catch {
        // ignore
      }
    }
    return null;
  }

  isAuthenticated(): boolean {
    const token = this.getCookie('auth_token');
    const user = this.getCurrentUser();
    if (token && user) return true;
    const localToken = this.getLocal('auth_token');
    return !!(localToken && user);
  }

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user?.role === role;
  }

  getAuthToken(): string | null {
    return this.getCookie('auth_token') || this.getLocal('auth_token');
  }

  async refreshToken(): Promise<boolean> {
    return false;
  }
}

const authService = new AuthService();

export default authService;
export type { LoginCredentials, RegisterData, User, AuthResponse };
